import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2, Pause, Play, Settings, ShieldAlert, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import {
  type AssignmentSet,
  type DeviceDetail,
  type DeviceSummary,
  archiveDevice,
  getDevice,
  listDevices,
  listAssignmentSets,
  updateDeviceAssignmentSets,
  updateDeviceConfig,
  updateDeviceState
} from '@/lib/web-api';
import type { RuntimeConfig } from '@/lib/runtime-config';

type DevicesPageProps = {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
};

const statusLabel = (status?: DeviceSummary['config_status']): string => {
  switch (status) {
    case 'up_to_date':
      return 'Up to date';
    case 'stale':
      return 'Out of date';
    default:
      return 'Unknown';
  }
};

const statusVariant = (status?: DeviceSummary['config_status']): 'success' | 'warning' | 'default' => {
  switch (status) {
    case 'up_to_date':
      return 'success';
    case 'stale':
      return 'warning';
    default:
      return 'default';
  }
};

const deviceStateLabel = (state?: DeviceSummary['device_state']): string => {
  switch (state) {
    case 'paused':
      return 'Paused';
    case 'wipe_requested':
      return 'Wipe requested';
    case 'wiped':
      return 'Wiped';
    case 'archived':
      return 'Archived';
    case 'active':
      return 'Active';
    default:
      return 'Unknown';
  }
};

const deviceStateVariant = (state?: DeviceSummary['device_state']): 'success' | 'warning' | 'error' | 'outline' | 'default' => {
  switch (state) {
    case 'active':
      return 'success';
    case 'paused':
    case 'wipe_requested':
      return 'warning';
    case 'wiped':
      return 'error';
    case 'archived':
      return 'outline';
    default:
      return 'default';
  }
};

const riskLabel = (risk?: DeviceSummary['health_risk_level']): string => {
  switch (risk) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return 'Unknown';
  }
};

const riskVariant = (risk?: DeviceSummary['health_risk_level']): 'success' | 'warning' | 'error' | 'outline' | 'default' => {
  switch (risk) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'outline';
    case 'low':
      return 'success';
    default:
      return 'default';
  }
};

const formatDurationSeconds = (seconds?: number): string => {
  if (!Number.isFinite(seconds) || (seconds as number) < 0) {
    return '—';
  }
  const total = Math.floor(seconds as number);
  if (total < 60) {
    return `${total}s`;
  }
  const mins = Math.floor(total / 60);
  if (mins < 60) {
    return `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `${hours}h` : `${hours}h ${remMins}m`;
};

const normalizeIds = (ids: string[]): string[] => {
  const unique = Array.from(new Set(ids));
  unique.sort();
  return unique;
};

type SortDirection = 'asc' | 'desc';

type DeviceSortColumn =
  | 'device'
  | 'os'
  | 'client'
  | 'user'
  | 'last_seen'
  | 'config_sync'
  | 'state'
  | 'risk'
  | 'key_rotated'
  | 'status'
  | 'manage';

const compareText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

const compareNumber = (a: number, b: number): number => a - b;

const compareOptional = <T,>(
  a: T | undefined,
  b: T | undefined,
  compare: (left: T, right: T) => number,
  direction: SortDirection
): number => {
  const aMissing = typeof a === 'undefined';
  const bMissing = typeof b === 'undefined';
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  const base = compare(a, b);
  return direction === 'asc' ? base : -base;
};

const statusRank = (status?: DeviceSummary['config_status']): number => {
  switch (status) {
    case 'up_to_date':
      return 0;
    case 'stale':
      return 1;
    default:
      return 2;
  }
};

const stateRank = (state?: DeviceSummary['device_state']): number => {
  switch (state) {
    case 'active':
      return 0;
    case 'paused':
      return 1;
    case 'wipe_requested':
      return 2;
    case 'wiped':
      return 3;
    case 'archived':
      return 4;
    default:
      return 5;
  }
};

const riskRank = (risk?: DeviceSummary['health_risk_level']): number => {
  switch (risk) {
    case 'low':
      return 0;
    case 'medium':
      return 1;
    case 'high':
      return 2;
    case 'critical':
      return 3;
    default:
      return 4;
  }
};

const sortDevices = (
  devices: DeviceSummary[],
  column: DeviceSortColumn,
  direction: SortDirection
): DeviceSummary[] => {
  const sorted = [...devices];
  sorted.sort((left, right) => {
    let result = 0;
    switch (column) {
      case 'device': {
        const leftName = left.device_name ?? left.device_id;
        const rightName = right.device_name ?? right.device_id;
        result = direction === 'asc'
          ? compareText(leftName, rightName)
          : compareText(rightName, leftName);
        break;
      }
      case 'os': {
        const leftOs = [left.platform, left.os_version].filter(Boolean).join(' ') || undefined;
        const rightOs = [right.platform, right.os_version].filter(Boolean).join(' ') || undefined;
        result = compareOptional(leftOs, rightOs, compareText, direction);
        break;
      }
      case 'client':
        result = compareOptional(left.client_version, right.client_version, compareText, direction);
        break;
      case 'user':
        result = compareOptional(left.user_upn, right.user_upn, compareText, direction);
        break;
      case 'last_seen':
        result = compareOptional(left.last_seen_unix_ms, right.last_seen_unix_ms, compareNumber, direction);
        break;
      case 'config_sync':
        result = compareOptional(left.last_config_sync_unix_ms, right.last_config_sync_unix_ms, compareNumber, direction);
        break;
      case 'state':
        result = direction === 'asc'
          ? compareNumber(stateRank(left.device_state), stateRank(right.device_state))
          : compareNumber(stateRank(right.device_state), stateRank(left.device_state));
        break;
      case 'risk':
        result = direction === 'asc'
          ? compareNumber(riskRank(left.health_risk_level), riskRank(right.health_risk_level))
          : compareNumber(riskRank(right.health_risk_level), riskRank(left.health_risk_level));
        break;
      case 'key_rotated':
        result = compareOptional(left.peer_key_last_rotated_unix_ms, right.peer_key_last_rotated_unix_ms, compareNumber, direction);
        break;
      case 'status':
        result = direction === 'asc'
          ? compareNumber(statusRank(left.config_status), statusRank(right.config_status))
          : compareNumber(statusRank(right.config_status), statusRank(left.config_status));
        break;
      case 'manage':
        result = direction === 'asc'
          ? compareText(left.device_id, right.device_id)
          : compareText(right.device_id, left.device_id);
        break;
      default:
        result = 0;
    }
    if (result !== 0) {
      return result;
    }
    return compareText(left.device_id, right.device_id);
  });
  return sorted;
};

export const DevicesPage = ({ runtimeConfig, tenantId }: DevicesPageProps) => {
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<DeviceSortColumn>('device');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [assignmentSets, setAssignmentSets] = useState<AssignmentSet[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(true);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<DeviceDetail | null>(null);
  const [configText, setConfigText] = useState('');
  const [saving, setSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [selectedSetIds, setSelectedSetIds] = useState<string[]>([]);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentSetError, setAssignmentSetError] = useState<string | null>(null);
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const deviceCount = devices.length;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredDevices = useMemo(() => {
    if (!normalizedSearchQuery) {
      return devices;
    }
    return devices.filter(device => {
      const deviceName = (device.device_name ?? '').toLowerCase();
      const deviceId = (device.device_id ?? '').toLowerCase();
      return deviceName.includes(normalizedSearchQuery) || deviceId.includes(normalizedSearchQuery);
    });
  }, [devices, normalizedSearchQuery]);
  const sortedDevices = useMemo(
    () => sortDevices(filteredDevices, sortColumn, sortDirection),
    [filteredDevices, sortColumn, sortDirection]
  );
  const filteredDeviceCount = filteredDevices.length;

  const toggleSort = useCallback((column: DeviceSortColumn) => {
    if (column === sortColumn) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  }, [sortColumn]);

  const sortIndicator = useCallback((column: DeviceSortColumn): string => {
    if (sortColumn !== column) {
      return '↕';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  }, [sortColumn, sortDirection]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listDevices(runtimeConfig, tenantId, { includeArchived });
      setDevices(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load devices.');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, runtimeConfig, setError, tenantId]);

  const fetchAssignmentSets = useCallback(async () => {
    setAssignmentLoading(true);
    setAssignmentError(null);
    try {
      const sets = await listAssignmentSets(runtimeConfig, tenantId);
      setAssignmentSets(sets);
    } catch (err) {
      setAssignmentError(err instanceof Error ? err.message : 'Unable to load assignment sets.');
    } finally {
      setAssignmentLoading(false);
    }
  }, [runtimeConfig, tenantId]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    void fetchAssignmentSets();
  }, [fetchAssignmentSets]);

  const openDevice = useCallback(async (deviceId: string) => {
    setDialogOpen(true);
    setSelected(null);
    setConfigText('');
    setConfigError(null);
    setAssignmentSetError(null);
    setActionError(null);
    try {
      const detail = await getDevice(runtimeConfig, tenantId, deviceId);
      setSelected(detail);
      setConfigText(detail.config ? JSON.stringify(detail.config.config, null, 2) : '{\n  \n}');
      setSelectedSetIds(detail.device.assignment_set_ids ?? []);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Unable to load device config.');
    }
  }, [runtimeConfig, tenantId]);

  const reloadSelected = useCallback(async () => {
    if (!selected?.device.device_id) {
      return;
    }
    try {
      const detail = await getDevice(runtimeConfig, tenantId, selected.device.device_id);
      setSelected(detail);
      setSelectedSetIds(detail.device.assignment_set_ids ?? []);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to refresh device details.');
    }
  }, [runtimeConfig, selected, tenantId]);

  const handleSave = useCallback(async () => {
    if (!selected?.device.device_id) {
      return;
    }
    setSaving(true);
    setConfigError(null);
    try {
      const parsed = JSON.parse(configText);
      await updateDeviceConfig(runtimeConfig, tenantId, selected.device.device_id, parsed);
      setDialogOpen(false);
      await fetchDevices();
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Unable to update config.');
    } finally {
      setSaving(false);
    }
  }, [configText, fetchDevices, runtimeConfig, selected, setSaving, tenantId]);

  const handleSaveAssignments = useCallback(async () => {
    if (!selected?.device.device_id) {
      return;
    }
    setAssignmentSaving(true);
    setAssignmentSetError(null);
    try {
      await updateDeviceAssignmentSets(runtimeConfig, tenantId, selected.device.device_id, selectedSetIds);
      await reloadSelected();
      await fetchDevices();
    } catch (err) {
      setAssignmentSetError(err instanceof Error ? err.message : 'Unable to update assignment sets.');
    } finally {
      setAssignmentSaving(false);
    }
  }, [fetchDevices, reloadSelected, runtimeConfig, selected, selectedSetIds, tenantId]);

  const selectedDeviceId = selected?.device.device_id;

  const handleStateChange = useCallback(async (deviceId: string, state: 'active' | 'paused' | 'wipe_requested') => {
    setActionSaving(true);
    setActionError(null);
    try {
      await updateDeviceState(runtimeConfig, tenantId, deviceId, state);
      if (selectedDeviceId === deviceId) {
        await reloadSelected();
      }
      await fetchDevices();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to update device state.');
    } finally {
      setActionSaving(false);
    }
  }, [fetchDevices, reloadSelected, runtimeConfig, selectedDeviceId, tenantId]);

  const handleArchive = useCallback(async (deviceId: string) => {
    setActionSaving(true);
    setActionError(null);
    try {
      await archiveDevice(runtimeConfig, tenantId, deviceId);
      const archivedFromOpenDialog = dialogOpen && selectedDeviceId === deviceId;
      if (archivedFromOpenDialog) {
        setDialogOpen(false);
      } else if (selectedDeviceId === deviceId) {
        await reloadSelected();
      }
      await fetchDevices();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Unable to archive device.');
    } finally {
      setActionSaving(false);
    }
  }, [dialogOpen, fetchDevices, reloadSelected, runtimeConfig, selectedDeviceId, tenantId]);

  const handlePauseOrResume = useCallback((device: DeviceSummary) => {
    if (!device.device_id) {
      return;
    }
    if (device.device_state === 'paused') {
      void handleStateChange(device.device_id, 'active');
      return;
    }
    if (!window.confirm('Pause this device? Peer sync will stop until you resume it.')) {
      return;
    }
    void handleStateChange(device.device_id, 'paused');
  }, [handleStateChange]);

  const handleWipe = useCallback((device: DeviceSummary) => {
    if (!device.device_id) {
      return;
    }
    if (!window.confirm('Wipe this device? This will erase local data and caches on the device.')) {
      return;
    }
    void handleStateChange(device.device_id, 'wipe_requested');
  }, [handleStateChange]);

  const handleArchiveWithWarning = useCallback((device: DeviceSummary) => {
    if (!device.device_id) {
      return;
    }
    if (!window.confirm('Archive this device? The device will still store local copies of synced data.')) {
      return;
    }
    void handleArchive(device.device_id);
  }, [handleArchive]);

  const selectedMeta = useMemo(() => {
    if (!selected) {
      return null;
    }
    return {
      name: selected.device.device_name || selected.device.device_id,
      platform: selected.device.platform || '—',
      osVersion: selected.device.os_version || '—',
      clientVersion: selected.device.client_version || '—',
      userUpn: selected.device.user_upn || '—',
      lastSeen: selected.device.last_seen_unix_ms
        ? formatRelativeTime(selected.device.last_seen_unix_ms)
        : '—',
      lastSync: selected.device.last_config_sync_unix_ms
        ? formatRelativeTime(selected.device.last_config_sync_unix_ms)
        : '—',
      configVersion: selected.config?.config_version ?? 0,
      updatedAt: selected.config?.updated_at_unix_ms
        ? formatDateTime(selected.config.updated_at_unix_ms)
        : '—',
      deviceState: selected.device.device_state,
      healthRisk: selected.device.health_risk_level,
      lastHeartbeat: selected.device.last_heartbeat_unix_ms
        ? formatRelativeTime(selected.device.last_heartbeat_unix_ms)
        : '—',
      pendingBackupAge: formatDurationSeconds(selected.device.pending_backup_age_seconds),
      consecutiveSyncFailures:
        typeof selected.device.consecutive_sync_failures === 'number'
          ? String(selected.device.consecutive_sync_failures)
          : '—',
      syncBacklogCount:
        typeof selected.device.sync_backlog_count === 'number'
          ? String(selected.device.sync_backlog_count)
          : '—',
      syncBacklogBytes:
        typeof selected.device.sync_backlog_bytes === 'number'
          ? `${selected.device.sync_backlog_bytes.toLocaleString()} B`
          : '—',
      snapshotHealth: selected.device.snapshot_health ?? '—',
      lastLogUpload: selected.device.last_log_upload_unix_ms
        ? formatRelativeTime(selected.device.last_log_upload_unix_ms)
        : '—',
      keyRotated: formatDateTime(selected.device.peer_key_last_rotated_unix_ms),
      pausedAt: formatDateTime(selected.device.paused_at_unix_ms),
      wipeRequestedAt: formatDateTime(selected.device.wipe_requested_at_unix_ms),
      wipeAppliedAt: formatDateTime(selected.device.wipe_applied_at_unix_ms),
      archivedAt: formatDateTime(selected.device.archived_at_unix_ms)
    };
  }, [selected]);

  const assignmentsDirty = useMemo(() => {
    if (!selected) {
      return false;
    }
    const current = normalizeIds(selected.device.assignment_set_ids ?? []);
    const next = normalizeIds(selectedSetIds);
    return JSON.stringify(current) !== JSON.stringify(next);
  }, [selected, selectedSetIds]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Control plane</p>
          <h2 className="text-2xl font-semibold">Devices</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-[260px]"
            placeholder="Search by device name or ID"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
          />
          <Button
            variant={includeArchived ? 'secondary' : 'outline'}
            onClick={() => setIncludeArchived(prev => !prev)}
            disabled={loading}
          >
            {includeArchived ? 'Hide archived' : 'Show archived'}
          </Button>
          <Button variant="outline" onClick={() => void fetchDevices()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="space-y-2">
          <CardTitle>Registered devices</CardTitle>
          <CardDescription>
            {filteredDeviceCount} of {deviceCount} device{deviceCount === 1 ? '' : 's'} reporting config sync status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading devices
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('device')}>
                      Device
                      <span aria-hidden>{sortIndicator('device')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('os')}>
                      OS
                      <span aria-hidden>{sortIndicator('os')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('client')}>
                      Client
                      <span aria-hidden>{sortIndicator('client')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('user')}>
                      User
                      <span aria-hidden>{sortIndicator('user')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('last_seen')}>
                      Last seen
                      <span aria-hidden>{sortIndicator('last_seen')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('config_sync')}>
                      Config sync
                      <span aria-hidden>{sortIndicator('config_sync')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('state')}>
                      State
                      <span aria-hidden>{sortIndicator('state')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('risk')}>
                      Risk
                      <span aria-hidden>{sortIndicator('risk')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('key_rotated')}>
                      Key rotated
                      <span aria-hidden>{sortIndicator('key_rotated')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => toggleSort('status')}>
                      Status
                      <span aria-hidden>{sortIndicator('status')}</span>
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" className="ml-auto flex items-center gap-1" onClick={() => toggleSort('manage')}>
                      Manage
                      <span aria-hidden>{sortIndicator('manage')}</span>
                    </button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDevices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-sm text-muted-foreground">
                      No devices match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedDevices.map(device => (
                    <TableRow key={device.device_id}>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            {device.device_name || device.device_id}
                          </p>
                          <p className="text-xs text-muted-foreground">{device.device_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm">{device.platform ?? '—'}</p>
                          <p className="text-xs text-muted-foreground">
                            {device.os_version ?? '—'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{device.client_version ?? '—'}</TableCell>
                      <TableCell>{device.user_upn ?? '—'}</TableCell>
                      <TableCell>
                        {device.last_seen_unix_ms ? formatRelativeTime(device.last_seen_unix_ms) : '—'}
                      </TableCell>
                      <TableCell>
                        {device.last_config_sync_unix_ms
                          ? formatRelativeTime(device.last_config_sync_unix_ms)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={deviceStateVariant(device.device_state)}>
                          {deviceStateLabel(device.device_state)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={riskVariant(device.health_risk_level)}>
                          {riskLabel(device.health_risk_level)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatDateTime(device.peer_key_last_rotated_unix_ms)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(device.config_status)}>
                          {statusLabel(device.config_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="secondary" disabled={actionSaving || device.device_state === 'archived'}>
                                Actions
                                <ChevronDown className="ml-2 h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                disabled={actionSaving || device.device_state === 'archived'}
                                onSelect={() => handlePauseOrResume(device)}
                              >
                                {device.device_state === 'paused' ? 'Resume' : 'Pause'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={actionSaving || device.device_state === 'archived'}
                                onSelect={() => handleWipe(device)}
                              >
                                Wipe
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={actionSaving || device.device_state === 'archived'}
                                onSelect={() => handleArchiveWithWarning(device)}
                              >
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => void openDevice(device.device_id)}
                            aria-label={`Configure ${device.device_name || device.device_id}`}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Device configuration</DialogTitle>
            <DialogDescription>
              Update the assigned config for this device. Changes are pushed immediately.
            </DialogDescription>
          </DialogHeader>
          {selectedMeta ? (
            <div className="space-y-4">
              <div className="grid gap-4 rounded-lg border border-border bg-muted/40 p-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Device</p>
                  <p className="font-medium">{selectedMeta.name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Platform</p>
                  <p className="font-medium">{selectedMeta.platform}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">OS version</p>
                  <p className="font-medium">{selectedMeta.osVersion}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Client version</p>
                  <p className="font-medium">{selectedMeta.clientVersion}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">User</p>
                  <p className="font-medium">{selectedMeta.userUpn}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last seen</p>
                  <p className="font-medium">{selectedMeta.lastSeen}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Config version</p>
                  <p className="font-medium">v{selectedMeta.configVersion}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last updated</p>
                  <p className="font-medium">{selectedMeta.updatedAt}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Device state</p>
                  <p className="font-medium">{deviceStateLabel(selectedMeta.deviceState)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Health risk</p>
                  <p className="font-medium">{riskLabel(selectedMeta.healthRisk)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last heartbeat</p>
                  <p className="font-medium">{selectedMeta.lastHeartbeat}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Pending backup age</p>
                  <p className="font-medium">{selectedMeta.pendingBackupAge}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Sync failures</p>
                  <p className="font-medium">{selectedMeta.consecutiveSyncFailures}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Backlog files</p>
                  <p className="font-medium">{selectedMeta.syncBacklogCount}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Backlog bytes</p>
                  <p className="font-medium">{selectedMeta.syncBacklogBytes}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Snapshot health</p>
                  <p className="font-medium">{selectedMeta.snapshotHealth}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Last log upload</p>
                  <p className="font-medium">{selectedMeta.lastLogUpload}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Key rotated</p>
                  <p className="font-medium">{selectedMeta.keyRotated}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Paused at</p>
                  <p className="font-medium">{selectedMeta.pausedAt}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Wipe requested</p>
                  <p className="font-medium">{selectedMeta.wipeRequestedAt}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Wipe applied</p>
                  <p className="font-medium">{selectedMeta.wipeAppliedAt}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Archived</p>
                  <p className="font-medium">{selectedMeta.archivedAt}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">Assignment sets</p>
                    <p className="text-xs text-muted-foreground">Select the repo groups for this device.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void fetchAssignmentSets()} disabled={assignmentLoading}>
                    Refresh sets
                  </Button>
                </div>

                {assignmentError ? (
                  <p className="text-sm text-destructive">{assignmentError}</p>
                ) : null}

                {assignmentLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading assignment sets
                  </div>
                ) : assignmentSets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assignment sets configured yet.</p>
                ) : (
                  <div className="grid gap-2 md:grid-cols-2">
                    {assignmentSets.map(set => (
                      <label key={set.set_id} className="flex items-start gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedSetIds.includes(set.set_id)}
                          onChange={() => {
                            setSelectedSetIds(prev => (
                              prev.includes(set.set_id)
                                ? prev.filter(id => id !== set.set_id)
                                : [...prev, set.set_id]
                            ));
                          }}
                        />
                        <span>
                          <span className="block font-medium">{set.set_name}</span>
                          <span className="block text-xs text-muted-foreground">{set.repo_ids.join(', ')}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {assignmentSetError ? (
                  <p className="text-sm text-destructive">{assignmentSetError}</p>
                ) : null}

                <div className="flex justify-end">
                  <Button
                    onClick={() => void handleSaveAssignments()}
                    disabled={assignmentSaving || assignmentLoading || !assignmentsDirty}
                  >
                    {assignmentSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving
                      </>
                    ) : (
                      'Save assignments'
                    )}
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Lifecycle actions</p>
                  <p className="text-xs text-muted-foreground">
                    Pause to stop peer sync, wipe to erase local data, or archive to remove this device without wiping local copies.
                  </p>
                </div>

                {actionError ? (
                  <p className="text-sm text-destructive">{actionError}</p>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  {selectedMeta.deviceState === 'paused' ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!selected) {
                          return;
                        }
                        handlePauseOrResume(selected.device);
                      }}
                      disabled={actionSaving}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Resume
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!selected) {
                          return;
                        }
                        handlePauseOrResume(selected.device);
                      }}
                      disabled={actionSaving || selectedMeta.deviceState === 'archived'}
                    >
                      <Pause className="mr-2 h-4 w-4" />
                      Pause
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!selected) {
                        return;
                      }
                      handleWipe(selected.device);
                    }}
                    disabled={actionSaving || selectedMeta.deviceState === 'archived'}
                  >
                    <ShieldAlert className="mr-2 h-4 w-4" />
                    Wipe
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!selected) {
                        return;
                      }
                      handleArchiveWithWarning(selected.device);
                    }}
                    disabled={actionSaving || selectedMeta.deviceState === 'archived'}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium">Config JSON</p>
                <textarea
                  className="min-h-[260px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={configText}
                  onChange={event => setConfigText(event.target.value)}
                />
                {configError ? (
                  <p className="text-sm text-destructive">{configError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Paste the repo assignments and settings in JSON format.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading device configuration
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving || !selectedMeta}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving
                </>
              ) : (
                'Save config'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
